import { toastError } from '@onestep/coreui/Toast';
import classNames from 'classnames';
import { StatusCodes } from 'http-status-codes';
import { isEmpty } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';

import useQueryParam from '../../../../../../../hooks/useCustomQueryParam';
import useMixpanel from '../../../../../../../hooks/useMixpanel';
import useEMRPatientsService, {
  emrType,
} from '../../../../../../../services/intakeDataService/emrPatientsService';
import type { IEMRPatient } from '../../../../../../../services/intakeDataService/emrPatientsService/types';
import { LoadingPlaceholder } from '../../../../../../LoadingPlaceholder';
import NextAction from '../../../OnBoardingDrawerAction/NextAction';
import { useOnBoardingDrawer } from '../../../OnBoardingDrawerProvider';
import DuplicatedEMRResult from './DuplicatedEMRResult';
import EmptyEMRResults from './EmptyEMRResults';
import EpicSearchForm from './EmrForms/EpicSearchForm.tsx';
import PointClickSearchForm from './EmrForms/PointClickSearchForm';
import EMRPatientResults from './EMRPatientResults';
import styles from './index.module.scss';

const EMRImport = ({ emr }: { emr: emrType }) => {
  const { track } = useMixpanel();
  const { moveToStep, setContextData, activeStep } = useOnBoardingDrawer();
  const [, setNewPatient] = useQueryParam<string | undefined>('newPatientUuid');
  const [selected, setSelected] = useState<number | undefined>();
  const {
    EMRPatientsList,
    importEMRPatient,
    updateSearchParams,
    searchParams,
    state: { isLoading, isError, error, isRefetching },
  } = useEMRPatientsService(emr);

  const [duplicatedPateintUuid, setDuplicatedPateintUuid] = useState<string>('');
  const ref = useRef<HTMLDivElement>(null);

  const selectedPatient =
    EMRPatientsList?.find((item) => item.patientId === selected) ?? ({} as IEMRPatient);
  const importPatient = async () => {
    try {
      const uuid = await importEMRPatient(selected as number);
      if (uuid) {
        setContextData({
          patient: {
            uuid,
          },
        });
        moveToStep(activeStep);
      } else {
        setNewPatient(undefined);
      }
    } catch (error) {
      if (error.response.status === StatusCodes.CONFLICT) {
        setDuplicatedPateintUuid(error.response?.data?.patientUuid);
        ref?.current?.scrollTo({ top: 0 }); // scroll top to see duplicate error message
      } else {
        toastError({ content: 'Failed to import EMR patient' });
      }
    }
  };

  useEffect(() => {
    if (isError) {
      toastError({
        content: 'Failed to fetch EMR patients!',
        toastId: 'failed_get_emr_patients_list',
      });
      track('failed_get_emr_patients_list', {
        error,
      });
    }
  }, [isError]);

  useEffect(() => {
    setSelected(undefined);
  }, []);

  return (
    <div className={classNames(styles.root, 'flex flex-col h-full gap-4')}>
      <div
        className={classNames(
          styles.enterDetails,
          'rounded-8 flex flex-col items-center',
        )}
      >
        {emr === emrType.pointClick || emr === emrType.yardi ? (
          <PointClickSearchForm updateSearchParams={updateSearchParams} />
        ) : emr === emrType.epic ? (
          <EpicSearchForm updateSearchParams={updateSearchParams} />
        ) : null}
      </div>
      <div className={'flex flex-col gap-4 overflow-auto'} ref={ref}>
        <LoadingPlaceholder isLoading={!!searchParams && (isLoading || isRefetching)}>
          {duplicatedPateintUuid ? (
            <DuplicatedEMRResult patientUuid={duplicatedPateintUuid} patient={selectedPatient} />
          ) : null}
          <EMRPatientResults
            selected={selected}
            onChangeResults={(id) => {
              setSelected(id);
              if (duplicatedPateintUuid) setDuplicatedPateintUuid('');
            }}
            EMRPatientsList={EMRPatientsList}
          />
          {isEmpty(EMRPatientsList) ? <EmptyEMRResults isEmpty={isEmpty(searchParams)} /> : null}
        </LoadingPlaceholder>
      </div>
      <NextAction
        testid={'emr-import-proceed'}
        disabled={!selected || !!duplicatedPateintUuid}
        onClick={importPatient}
      />
    </div>
  );
};

export default observer(EMRImport);
